'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { authApi, type ApiError } from '@/lib/api';

interface User {
  _id: string;
  email: string;
  businessName: string;
  industry: string;
  role?: 'USER' | 'ADMIN' | 'QA';
  subscription?: {
    plan: string;
    status: string;
    maxPhoneNumbers: number;
    maxConcurrentCalls: number;
    minutesIncluded: number;
    minutesUsed: number;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    cancelAtPeriodEnd: boolean;
  };
  usage?: {
    phoneNumbersUsed: number;
    currentConcurrentCalls: number;
    totalCallsThisMonth: number;
    totalMinutesThisMonth: number;
  };
  settings?: {
    notificationEmail?: string;
    notificationPhone?: string;
    notificationPreferences?: {
      enableEmailNotifications: boolean;
      enableSmsAlerts: boolean;
      sendDailyReports: boolean;
      sendWeeklyReports: boolean;
      nexhealthDefaults?: {
        subdomain: string;
        apiKey: string;
        locationId?: number;
      };
    };
    language: string;
  };
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isPaidUser: boolean;
  isAdmin: boolean;
  isQA: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  // Paid users are active subscriptions only
  const isPaidUser = user?.subscription?.status === 'active';
  const isAdmin = user?.role === 'ADMIN';
  const isQA = user?.role === 'QA';

  // Fetch current user on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authApi.getCurrentUser();
      setUser(response.user);
    } catch (error) {
      // User not authenticated
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    setUser(response.user);
    if (response.user?.role === 'QA') {
      router.push('/qa');
    } else {
      router.push('/dashboard');
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      router.push('/');
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authApi.getCurrentUser();
      setUser(response.user);
    } catch (error) {
      console.error('Refresh user error:', error);
      setUser(null);
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isPaidUser,
    isAdmin,
    isQA,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
